/**
 * The typefaces a report can be set in.
 *
 * **This is a property of the document, not of the reader.** Two people
 * opening the same report see the same thing, and the PDF matches what was on
 * screen. A typeface remembered per browser would mean a report that looks
 * different to the person who approves it than to the person who wrote it,
 * which is exactly the sort of surprise a signed document must not contain.
 *
 * The built-in list is short on purpose. Five typefaces that are each right
 * for something beat forty that mostly are not, and every one here is a text
 * face with a full weight range, real small figures, and tabular numerals: a
 * display face would look striking on a cover and fall apart in a balance
 * sheet.
 *
 * Beyond those, any family of the firm's font library (uploaded, or taken
 * from Google Fonts by the server), named `lib:<its id>`. The page draws it
 * under a family name of its own (`dmf-<id>`), so it can never be confused
 * with a font of the same name installed on somebody's machine.
 *
 * Pure. Names and stacks only; loading them is somebody else's job.
 */

/** The typefaces that ship with the application. */
export type BuiltinTypefaceId = 'inter' | 'source-serif' | 'lora' | 'plex-sans' | 'garamond'

/** A family of the font library, by its id there. */
export type LibraryTypefaceId = `lib:${number}`

/** Which typeface a report is set in. */
export type TypefaceId = BuiltinTypefaceId | LibraryTypefaceId

export interface Typeface {
  readonly id: TypefaceId
  /** The family name the loaded font registers under. */
  readonly family: string
  /** What to fall back to while it loads, and if it never does. */
  readonly stack: string
  /** Sans or serif, so the chooser can group them. */
  readonly kind: 'sans' | 'serif'
}

const SANS_FALLBACK = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
const SERIF_FALLBACK = "Georgia, 'Times New Roman', serif"

export const TYPEFACES: readonly (Typeface & { readonly id: BuiltinTypefaceId })[] = [
  {
    id: 'inter',
    family: 'Inter Variable',
    stack: `'Inter Variable', Inter, ${SANS_FALLBACK}`,
    kind: 'sans',
  },
  {
    id: 'source-serif',
    family: 'Source Serif 4 Variable',
    stack: `'Source Serif 4 Variable', 'Source Serif 4', ${SERIF_FALLBACK}`,
    kind: 'serif',
  },
  {
    id: 'lora',
    family: 'Lora Variable',
    stack: `'Lora Variable', Lora, ${SERIF_FALLBACK}`,
    kind: 'serif',
  },
  {
    id: 'plex-sans',
    family: 'IBM Plex Sans Variable',
    stack: `'IBM Plex Sans Variable', 'IBM Plex Sans', ${SANS_FALLBACK}`,
    kind: 'sans',
  },
  {
    id: 'garamond',
    family: 'EB Garamond Variable',
    stack: `'EB Garamond Variable', 'EB Garamond', ${SERIF_FALLBACK}`,
    kind: 'serif',
  },
]

/**
 * What a report is set in unless it says otherwise.
 *
 * Also what every report written before this setting existed is shown in, so
 * changing it would restyle work that is already signed. It does not change.
 */
export const DEFAULT_TYPEFACE: TypefaceId = 'inter'

const BY_ID = new Map<string, Typeface>(TYPEFACES.map((face) => [face.id, face]))

const LIBRARY = /^lib:([1-9]\d{0,8})$/

/** The library family a typeface names, or null for a built-in one. */
export function libraryFamilyId(id: string): number | null {
  const match = LIBRARY.exec(id)
  return match ? Number(match[1]) : null
}

export function libraryTypefaceId(familyId: number): LibraryTypefaceId {
  return `lib:${familyId}`
}

/** The family name a library family is drawn under on the page. */
export function libraryFontFamily(familyId: number): string {
  return `dmf-${familyId}`
}

export function isBuiltinTypeface(id: string): id is BuiltinTypefaceId {
  return BY_ID.has(id)
}

/**
 * A typeface this version can draw: a built-in one, or a library family. A
 * library family since deleted still names one, and is drawn in the stand-in.
 */
export function isTypefaceId(value: string): value is TypefaceId {
  return BY_ID.has(value) || LIBRARY.test(value)
}

/**
 * The typeface with this id, or the default.
 *
 * Never fails. A document naming a typeface this version does not know is
 * still a document somebody needs to read, and showing it in the default beats
 * refusing to draw it.
 */
export function typefaceOf(id: string): Typeface {
  const library = libraryFamilyId(id)
  if (library !== null) {
    const family = libraryFontFamily(library)
    // While it loads, and if it never does: the default face, then the system's.
    const fallback = BY_ID.get(DEFAULT_TYPEFACE)?.stack ?? SANS_FALLBACK
    return {
      id: libraryTypefaceId(library),
      family,
      stack: `'${family}', ${fallback}`,
      kind: 'sans',
    }
  }
  return BY_ID.get(id) ?? BY_ID.get(DEFAULT_TYPEFACE)!
}

/**
 * Every typeface a document is drawn in: its text, its headings, its named
 * text styles. What has to be loaded for it to be seen, and embedded for it to
 * be exported.
 */
export function typefacesInUse(
  typeface: TypefaceId,
  headings: TypefaceId | 'document',
  styles: readonly { readonly typeface: TypefaceId }[],
): TypefaceId[] {
  const used = [typeface, ...(headings === 'document' ? [] : [headings])]
  return [...new Set([...used, ...styles.map((style) => style.typeface)])]
}
